/**
 * Feature 073 — Reusable PDF documents management widget.
 *
 * Used in two surfaces:
 *   • Admin SP editor (Information tab → Documents section, mode='admin')
 *   • SP self-edit dashboard (Profile tab → Documents section, mode='sp-self')
 *
 * Documents are written eagerly per-action against their own endpoints — they
 * are NOT part of the React Hook Form profile-edit lifecycle, so there is no
 * "unsaved changes" coupling and no save button to wire.
 */

import { useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createAdminProviderDocument,
  createMyProviderDocument,
  deleteAdminProviderDocument,
  deleteMyProviderDocument,
  listAdminProviderDocuments,
  listMyProviderDocuments,
  updateAdminProviderDocument,
  updateMyProviderDocument,
  type CreateProviderDocumentInput,
  type UpdateProviderDocumentInput,
} from '../api/providerDocumentsApi';
import type { OwnerProviderDocument } from '../types/provider-profile.types';
import { ProviderDocumentUploadForm } from './documents/ProviderDocumentUploadForm';
import { ProviderDocumentList } from './documents/ProviderDocumentList';
import { ProviderDocumentEditDialog } from './documents/ProviderDocumentEditDialog';

type Mode = 'admin' | 'sp-self';

type ProviderDocumentsManagerProps = {
  mode: Mode;
  /** Required when mode==='admin'; ignored when mode==='sp-self'. */
  providerId?: string;
};

export function ProviderDocumentsManager({
  mode,
  providerId,
}: ProviderDocumentsManagerProps) {
  const queryClient = useQueryClient();
  const queryKey =
    mode === 'admin'
      ? (['provider-documents', 'admin', providerId] as const)
      : (['provider-documents', 'me'] as const);

  const list = useQuery({
    queryKey,
    queryFn: () =>
      mode === 'admin'
        ? listAdminProviderDocuments(providerId as string)
        : listMyProviderDocuments(),
    enabled: mode === 'sp-self' || Boolean(providerId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const create = useMutation({
    mutationFn: (input: CreateProviderDocumentInput) =>
      mode === 'admin'
        ? createAdminProviderDocument(providerId as string, input)
        : createMyProviderDocument(input),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({
      docId,
      input,
    }: {
      docId: string;
      input: UpdateProviderDocumentInput;
    }) =>
      mode === 'admin'
        ? updateAdminProviderDocument(providerId as string, docId, input)
        : updateMyProviderDocument(docId, input),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (docId: string) =>
      mode === 'admin'
        ? deleteAdminProviderDocument(providerId as string, docId)
        : deleteMyProviderDocument(docId),
    onSuccess: invalidate,
  });

  const [editing, setEditing] = useState<OwnerProviderDocument | null>(null);

  return (
    <div className="space-y-4" data-testid="provider-documents-manager">
      <ProviderDocumentUploadForm
        isSaving={create.isPending}
        onSubmit={(input) => create.mutateAsync(input).then(() => undefined)}
      />

      <ProviderDocumentList
        documents={list.data ?? []}
        isLoading={list.isLoading}
        onEdit={(doc) => setEditing(doc)}
        onDelete={(doc) => {
          if (
            window.confirm(
              `Delete "${doc.displayName}"? Visitors will no longer see this PDF.`,
            )
          ) {
            remove.mutate(doc.id);
          }
        }}
      />

      {editing ? (
        <ProviderDocumentEditDialog
          doc={editing}
          isSaving={update.isPending}
          onCancel={() => setEditing(null)}
          onSave={async (input) => {
            await update.mutateAsync({ docId: editing.id, input });
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}
